import { api } from '../lib/api-client.js';

import type {
  BuildingDocumentCategory,
  BuildingDocumentConfirmInput,
  BuildingDocumentPresignInput,
  BuildingFiltersQuery,
  BulkCreateUnitsInput,
  CreateBuildingContactInput,
  CreateBuildingInput,
  CreateUnitInput,
  ImportJobSummary,
  QuickSearchQuery,
  UpdateBuildingContactInput,
  UpdateBuildingInput,
  UpdateUnitInput,
} from '@sitewise/shared';

export interface BuildingSummary {
  id: string;
  orgId: string;
  name: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  borough: string | null;
  bin: string | null;
  bbl: string | null;
  latitude: number | null;
  longitude: number | null;
  geocodedAt: string | null;
  geocodeSource: string | null;
  geocodeConfidence: string | null;
  unitCountOverride: number | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  unitCount: number;
  openWorkOrders: number;
  openViolations: number;
}

export interface BuildingDetail {
  building: Omit<BuildingSummary, 'unitCount'>;
  unitCount: number;
  contactCount: number;
  stats: {
    openWorkOrders: number | null;
    openViolations: number | null;
    compliance: number | null;
  };
}

export interface UnitRow {
  id: string;
  orgId: string;
  buildingId: string;
  unitNumber: string;
  floor: string | null;
  unitType: string | null;
  occupancyStatus: string;
  bedroomCount: number | null;
  bathroomCount: number | null;
  squareFeet: number | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  // Migration 1: utility / appliance / lead-status snapshot. The
  // building Units tab renders these inline, the unit detail page
  // expands them. Every field is nullable until the user fills it in.
  gasService: boolean | null;
  electricService: boolean | null;
  heatSource: string | null;
  hotWaterSource: string | null;
  stoveFuel: string | null;
  stoveLastReplaced: string | null;
  refrigeratorLastReplaced: string | null;
  dishwasherPresent: boolean | null;
  dishwasherLastReplaced: string | null;
  washerDryerPresent: boolean | null;
  washerDryerLastReplaced: string | null;
  windowAcCount: number | null;
  centralAc: boolean | null;
  leadStatus: string | null;
  leadStatusUpdatedAt: string | null;
  /** Decoration added by `GET /buildings/:id/units` so the table can
   *  render the "Open WOs" column without a per-row fetch. Detail
   *  payloads also include it via the page-level aggregator. */
  openWorkOrderCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ContactRow {
  id: string;
  orgId: string;
  buildingId: string;
  name: string;
  roleLabel: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actorUserId: string | null;
  actorKind: string;
  metadata: unknown;
  createdAt: string;
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

export async function listBuildings(
  filters: Partial<BuildingFiltersQuery> = {},
): Promise<{ buildings: BuildingSummary[]; nextCursor: string | null }> {
  return api(`/buildings${qs(filters as Record<string, string | undefined>)}`);
}

export async function fetchBuilding(id: string): Promise<BuildingDetail> {
  return api(`/buildings/${id}`);
}

export async function createBuilding(input: CreateBuildingInput) {
  return api<{
    building: Omit<BuildingSummary, 'unitCount'>;
    contactCount: number;
    geocoded: boolean;
  }>('/buildings', { method: 'POST', body: input });
}

export async function updateBuilding(id: string, input: UpdateBuildingInput) {
  return api<{ building: Omit<BuildingSummary, 'unitCount'>; geocoded: boolean }>(
    `/buildings/${id}`,
    { method: 'PATCH', body: input },
  );
}

export async function deleteBuilding(id: string): Promise<void> {
  await api(`/buildings/${id}`, { method: 'DELETE' });
}

export async function regeocodeBuilding(id: string) {
  return api<{ building: Omit<BuildingSummary, 'unitCount'>; geocoded: boolean }>(
    `/buildings/${id}/regeocode`,
    { method: 'POST', body: {} },
  );
}

export async function listUnits(buildingId: string): Promise<{ units: UnitRow[] }> {
  return api(`/buildings/${buildingId}/units`);
}

export async function createUnit(buildingId: string, input: CreateUnitInput) {
  return api<{ unit: UnitRow }>(`/buildings/${buildingId}/units`, {
    method: 'POST',
    body: input,
  });
}

export interface UnitImportPreviewRow {
  rowNumber: number;
  unitNumber: string | null;
  outcome: 'new' | 'existing' | 'invalid';
  message: string | null;
  fields: string[];
}

export interface UnitImportPreview {
  rows: UnitImportPreviewRow[];
  summary: { total: number; new: number; existing: number; invalid: number };
}

export interface UnitImportResult {
  inserted: number;
  updated: number;
  ignored: number;
  unchanged: number;
}

export async function previewUnitImport(buildingId: string, csv: string): Promise<UnitImportPreview> {
  return api(`/buildings/${buildingId}/units/import/preview`, {
    method: 'POST',
    body: { csv },
  });
}

export async function commitUnitImport(
  buildingId: string,
  csv: string,
  duplicatePolicy: 'override' | 'ignore',
): Promise<UnitImportResult> {
  return api(`/buildings/${buildingId}/units/import`, {
    method: 'POST',
    body: { csv, duplicatePolicy },
  });
}

export async function bulkCreateUnits(buildingId: string, input: BulkCreateUnitsInput) {
  return api<{ requested: number; inserted: number; skipped: number; units: UnitRow[] }>(
    `/buildings/${buildingId}/units/bulk`,
    { method: 'POST', body: input },
  );
}

export async function updateUnit(unitId: string, input: UpdateUnitInput) {
  return api<{ unit: UnitRow }>(`/units/${unitId}`, { method: 'PATCH', body: input });
}

export async function deleteUnit(unitId: string): Promise<void> {
  await api(`/units/${unitId}`, { method: 'DELETE' });
}

export async function listContacts(buildingId: string): Promise<{ contacts: ContactRow[] }> {
  return api(`/buildings/${buildingId}/contacts`);
}

export async function createContact(buildingId: string, input: CreateBuildingContactInput) {
  return api<{ contact: ContactRow }>(`/buildings/${buildingId}/contacts`, {
    method: 'POST',
    body: input,
  });
}

export async function updateContact(contactId: string, input: UpdateBuildingContactInput) {
  return api<{ contact: ContactRow }>(`/building-contacts/${contactId}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function deleteContact(contactId: string): Promise<void> {
  await api(`/building-contacts/${contactId}`, { method: 'DELETE' });
}

export async function fetchActivity(buildingId: string, limit = 50): Promise<{ entries: ActivityEntry[] }> {
  return api(`/buildings/${buildingId}/activity${qs({ limit })}`);
}

export async function quickSearch(input: QuickSearchQuery): Promise<{
  buildings: Array<{ id: string; name: string; addressLine1: string; city: string }>;
  units: Array<{ id: string; unitNumber: string; buildingId: string; buildingName: string }>;
  workOrders: Array<{
    id: string;
    displayNumber: string;
    title: string;
    status: string;
    severity: string;
  }>;
  inspectionTemplates: Array<{
    id: string;
    name: string;
    appliesTo: string;
    status: string;
  }>;
  inspections: Array<{
    id: string;
    status: string;
    scheduledFor: string | null;
    templateId: string;
    buildingId: string;
  }>;
}> {
  return api(`/search/quick${qs(input as Record<string, string | number>)}`);
}

export async function listImports(): Promise<{ imports: ImportJobSummary[] }> {
  return api('/imports');
}

export async function fetchImport(id: string): Promise<{ import: ImportJobSummary }> {
  return api(`/imports/${id}`);
}

export async function createBuildingImport(input: { uploadId: string; label?: string }) {
  return api<{ import: ImportJobSummary }>('/imports/buildings', {
    method: 'POST',
    body: input,
  });
}

export async function commitImport(id: string, ackWarnings: boolean): Promise<void> {
  await api(`/imports/${id}/commit`, { method: 'POST', body: { ackWarnings } });
}

export async function cancelImport(id: string): Promise<void> {
  await api(`/imports/${id}/cancel`, { method: 'POST' });
}

/* ----------------------------- Building Records ---------------------- */

export interface BuildingDocument {
  id: string;
  buildingId: string;
  category: BuildingDocumentCategory;
  year: number;
  storageKey: string;
  originalFilename: string;
  contentType: string;
  bytes: number;
  caption: string | null;
  uploadedByUserId: string | null;
  createdAt: string;
}

export async function listBuildingDocuments(
  buildingId: string,
  filters: { category?: BuildingDocumentCategory; year?: number } = {},
) {
  return api<{ documents: BuildingDocument[]; total: number }>(
    `/buildings/${buildingId}/documents${qs(filters)}`,
  );
}

export interface PresignBuildingDocumentResponse {
  document: BuildingDocument;
  uploadId: string;
  uploadUrl: string;
  objectKey: string;
  headers: Record<string, string>;
  expiresAt: string;
}

export async function presignBuildingDocument(
  buildingId: string,
  input: BuildingDocumentPresignInput,
) {
  return api<PresignBuildingDocumentResponse>(`/buildings/${buildingId}/documents/presign`, {
    method: 'POST',
    body: input,
  });
}

export async function confirmBuildingDocument(
  buildingId: string,
  documentId: string,
  input: BuildingDocumentConfirmInput = {},
) {
  return api<{ document: BuildingDocument }>(
    `/buildings/${buildingId}/documents/${documentId}/confirm`,
    { method: 'POST', body: input },
  );
}

export async function fetchBuildingDocumentUrl(buildingId: string, documentId: string) {
  return api<{ url: string; expiresAt: string }>(
    `/buildings/${buildingId}/documents/${documentId}/url`,
  );
}

export async function deleteBuildingDocument(
  buildingId: string,
  documentId: string,
): Promise<void> {
  await api(`/buildings/${buildingId}/documents/${documentId}`, { method: 'DELETE' });
}
